// Used in teacher.module.ts, login-routing.module.ts, teacher-class.module.ts, distributor.module.ts, student-module.ts

// Angular
import { Injectable } from "@angular/core";
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";

// Services/Types/Helpers
import { LocalStorage } from "#services/local-storage.service";
import { PPUser as User } from "cli-types";
import { AuthHelper } from "#core/helpers/auth.helper";

// RXJS/Store
import { Store, select } from "@ngrx/store";
import * as LoginStore from "../../login/store";

@Injectable()
export class AuthGuardService  {
    viewAsStudent = false;

    constructor(
        private authHelper: AuthHelper,
        private router: Router,
        private loginStore: Store<LoginStore.UserState>,
        private localStorage: LocalStorage
    ) {}

    // used on parent routes
    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        const url: string = state.url;

        return this.verifyUser(url, route.queryParams.clref);
    }

    // used on child routes
    canActivateChild(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        const url: string = state.url;

        return this.verifyUser(url, route.queryParams.clref);
    }

    /**
     * @method verifyUser
     * @description 1) checks if token hasn't expired,
     *              2) checks if they have access to parent route,
     *              3) checks if user profile needs to be reloaded
     * @param {string} url
     * @param {string} routeOverride
     * @returns {boolean}
     */
    verifyUser(url: string, routeOverride: string): boolean {
        // Check if Teacher is viewing as Student
        this.loginStore
            .pipe(select(LoginStore.isViewAsStudent))
            .subscribe((result) => {
                this.viewAsStudent = result;
            });
        const localStorageProfileData = this.localStorage.get("userProfile");

        // Check if there is route override from another service indicated by URL query param 'clref'
        // Only used for student routing, teacher routing has too many complicated guards
        if (routeOverride && (url.toLowerCase().includes('student') || url.toLowerCase().includes('teacher'))) {
            // route override for direct linking

            if (url.toLowerCase().includes('teacher') &&
                (localStorageProfileData === undefined ||
                localStorageProfileData === null)) {
                this.loginStore.dispatch(LoginStore.userProfileRefresh());
            }
            return true;
        }

        // Use local storage user profile to determine if user is allowed access to a parent route
        if (
            localStorageProfileData === undefined ||
            localStorageProfileData === null
        ) {
            return false;
        }

        switch (localStorageProfileData.roleLevel) {
            case 1:
                if (url.substring(0, 13) !== "/distributor/") {
                    this.authHelper.redirectUser(localStorageProfileData);
                    return true;
                }
                break;
            case 2:
                if (url.substring(0, 9) !== "/teacher/") {
                    // TODO Need to change route after built
                    this.authHelper.redirectUser(localStorageProfileData);
                    return true;
                }
                break;
            case 3:
                if (url.substring(0, 9) !== "/teacher/") {
                    // TODO Need to change route after built
                    this.authHelper.redirectUser(localStorageProfileData);
                    return true;
                }
                break;
            case 4:
                // if viewAsStudent => break
                if (this.viewAsStudent) {
                    break;
                } else if (url.substring(0, 9) !== "/teacher/") {
                    this.authHelper.redirectUser(localStorageProfileData);
                    return true;
                }
                break;
            case 5:
                if (url.substring(0, 9) !== "/student/") {
                    this.authHelper.redirectUser(localStorageProfileData);
                    return true;
                }
                break;
            default:
                break;
        }

        let profileData: User | null = null;
        this.loginStore
            .pipe(select(LoginStore.getUserProfile))
            .subscribe((data) => {
                profileData = data;
            });

        // Checks if profile needs to be reloaded
        if (profileData === undefined || profileData === null) {
            this.loginStore.dispatch(LoginStore.userProfileRefresh());
        }
        return true;
    }
}
